import { useAchievements, useCommander } from "@/hooks/use-mock-data";
import { Crosshair, Shield, LayoutDashboard, Rocket, DollarSign, Gauge, Star, HelpCircle } from "lucide-react";

const iconMap: Record<string, typeof Crosshair> = {
  Crosshair,
  Shield,
  LayoutDashboard,
  Rocket,
  DollarSign,
  Gauge,
  Star,
};

export default function AchievementsPage() {
  const { data: achievements } = useAchievements();
  const { data: commander } = useCommander();

  const earned = achievements.filter((a) => a.earnedAt);
  const locked = achievements.filter((a) => !a.earnedAt);

  return (
    <div className="animate-fade-in max-w-4xl mx-auto px-6 py-6">
      <h1 className="font-brand text-4xl tracking-wide mb-2">TROPHY ROOM</h1>

      {/* XP Display */}
      <div className="flex items-center gap-4 mb-8">
        <div className="glass-card rounded-lg px-4 py-3 inline-flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">TOTAL XP</span>
          <span className="font-mono font-bold text-2xl text-electric-yellow">{commander.xpTotal}</span>
        </div>
        <div className="glass-card rounded-lg px-4 py-3 inline-flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">EARNED</span>
          <span className="font-mono font-bold text-2xl text-mint">{earned.length}/{achievements.length}</span>
        </div>
      </div>

      {/* Earned Achievements */}
      {earned.length > 0 && (
        <section className="mb-8">
          <h2 className="font-brand text-xl tracking-wide mb-4">EARNED</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {earned.map((ach) => {
              const Icon = iconMap[ach.icon] ?? Star;
              return (
                <div
                  key={ach.id}
                  className="glass-card rounded-lg p-4 relative overflow-hidden"
                  style={{ boxShadow: "0 0 20px rgba(68, 242, 210, 0.06)" }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-mint/10 flex items-center justify-center border border-mint/20">
                      <Icon className="w-5 h-5 text-mint" />
                    </div>
                    <div>
                      <h3 className="text-foreground font-medium text-sm">{ach.title}</h3>
                      <span className="text-[10px] text-mint font-mono">+{ach.xpReward} XP</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{ach.description}</p>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Earned {new Date(ach.earnedAt!).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Locked Achievements */}
      {locked.length > 0 && (
        <section>
          <h2 className="font-brand text-xl tracking-wide mb-4">LOCKED</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {locked.map((ach) => (
              <div
                key={ach.id}
                className="glass-card rounded-lg p-4 opacity-40"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-[rgba(255,255,255,0.06)] flex items-center justify-center border border-[rgba(255,255,255,0.08)]">
                    <HelpCircle className="w-5 h-5 text-[#555]" />
                  </div>
                  <div>
                    <h3 className="text-foreground font-medium text-sm">{ach.title}</h3>
                    <span className="text-[10px] text-[#555] font-mono">+{ach.xpReward} XP</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{ach.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
